import { apiGet, apiPatch, apiPost } from './client';

export interface SiteRecord {
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  domain: string | null;
  brand_id: string | null;
  template_id: number | null;
  status: string;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Whether the current tenant's site is new/empty (no existing HTML build, no
 * blocks, no raw_build blob). Drives the additive blocks-build routing: a
 * brand-new account is sent to the block-model builder; any site with content
 * keeps the legacy HTML build. `isEmpty` is authoritative + fail-safe server-side
 * (GET /sites/build-state).
 */
export interface SiteBuildState {
  isEmpty: boolean;
  hasHtml: boolean;
  hasBlocks: boolean;
  hasRawBuild: boolean;
}

export async function getSiteBuildState(): Promise<SiteBuildState> {
  return apiGet<SiteBuildState>('/sites/build-state');
}

export async function listSites(): Promise<SiteRecord[]> {
  const data = await apiGet<{ sites: SiteRecord[] }>('/sites');
  return (data.sites ?? []).map((site) => ({
    ...site,
    is_primary: Boolean(site.is_primary),
  }));
}

export async function getCurrentSite(): Promise<SiteRecord> {
  const data = await apiGet<{ site: SiteRecord }>('/sites/current');
  return {
    ...data.site,
    is_primary: Boolean(data.site.is_primary),
  };
}

export async function createSite(body: {
  name: string;
  slug?: string;
  domain?: string | null;
  brand_id?: string | null;
  template_id?: number | null;
  make_primary?: boolean;
}): Promise<SiteRecord> {
  const data = await apiPost<{ site: SiteRecord }>('/sites', body);
  return {
    ...data.site,
    is_primary: Boolean(data.site.is_primary),
  };
}

export async function updateSite(
  id: string,
  body: {
    name?: string;
    slug?: string;
    domain?: string | null;
    make_primary?: boolean;
    status?: 'draft' | 'active' | 'archived';
    /** Tie this site to a brand (sites.brand_id) — the tenant render prefers it over the tenant brand. */
    brand_id?: string | null;
  },
): Promise<SiteRecord> {
  const data = await apiPatch<{ site: SiteRecord }>(`/sites/${id}`, body);
  return {
    ...data.site,
    is_primary: Boolean(data.site.is_primary),
  };
}
